import { useState } from 'react';
import {
  CheckCircleOutlined, ClockCircleOutlined, DashboardOutlined, FallOutlined,
  FileSearchOutlined, PlusOutlined, ReloadOutlined, SyncOutlined,
} from '@ant-design/icons';
import type { TableProps } from 'antd';

import { Bento, DataTable, PageHeader, StatTile, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayVN, soVN } from '../../../lib/dinh-dang';
import { HopNopHoSo } from '../components/HopNopHoSo';
import { VungDuLieu } from '../components/VungDuLieu';
import { KET_LUAN, QUYEN } from '../constants';
import { useTongQuan } from '../hooks/useThamDinh';
import type { KetLuan, Phien, TieuChiHayTruot, TongQuan } from '../types';

/**
 * Tổng quan thẩm định (`/client/tham-dinh`), theo giao diện v3.
 *
 * Một lượt gọi `GET /tong-quan` trả đủ cả màn (đặc tả mục 13.6) — không ghép từ nhiều lời gọi, vì các con số
 * phải nhất quán với nhau tại cùng một thời điểm.
 *
 * Bốn ô số liệu như v3, không phải sáu: "Chờ phê duyệt" và "Lỗi" gộp vào hai khối dưới và cột Trạng thái của
 * màn Hồ sơ. Sáu ô ngang nhau làm người đọc phải quét cả hàng mới thấy cái cần chú ý.
 */
export function TongQuanThamDinhView() {
  const { t, locale } = useT();
  const dieuHuong = useDieuHuong();
  const q = useModulePermissions(QUYEN);
  const [moNop, setMoNop] = useState(false);
  const tq = useTongQuan();

  const cotChoDuyet: TableProps<Phien>['columns'] = [
    {
      title: t('thamDinh.phien.cotTieuDe'),
      dataIndex: 'tieu_de',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-content-primary">{r.tieu_de}</div>
          <div className="truncate text-2xs text-content-muted">
            {r.bo_ten} · {ngayVN(r.updated_at)}
          </div>
        </div>
      ),
    },
    {
      title: t('thamDinh.phien.cotDiem'),
      dataIndex: 'diem',
      width: 80,
      align: 'end',
      render: (v: number | null) => (v === null ? KHONG_CO : soVN(v, locale)),
    },
    {
      title: t('thamDinh.phien.cotKetLuan'),
      dataIndex: 'ket_luan',
      width: 150,
      render: (v: KetLuan | null) =>
        v ? <Badge variant={KET_LUAN[v].variant}>{t(KET_LUAN[v].khoa)}</Badge> : null,
    },
  ];

  const cotTruot: TableProps<TieuChiHayTruot>['columns'] = [
    {
      title: t('thamDinh.soLieu.cotTieuChi'),
      dataIndex: 'ma',
      render: (v: string) => <span className="font-mono text-xs text-content-primary">{v}</span>,
    },
    {
      title: t('thamDinh.soLieu.cotSoLan'),
      dataIndex: 'so_lan',
      width: 100,
      align: 'end',
      render: (v: number) => soVN(v, locale),
    },
    {
      title: t('thamDinh.soLieu.cotTyLeTruot'),
      dataIndex: 'ty_le_truot',
      width: 110,
      align: 'end',
      render: (v: number) => `${soVN(Math.round(v * 100), locale)}%`,
    },
  ];

  /** Thiếu lĩnh vực hoặc bộ phát hành thì chưa chấm được hồ sơ nào — nói thẳng việc cần làm. */
  function KhoiOnboarding({ du }: { du: TongQuan }) {
    if (du.co_linh_vuc && du.co_bo_phat_hanh) return null;
    const chuaLinhVuc = !du.co_linh_vuc;
    return (
      <div className="rounded-lg border border-warning-200 bg-warning-50 p-4">
        <p className="font-medium text-warning-800">
          {t(chuaLinhVuc ? 'thamDinh.soLieu.chuaCoLinhVuc' : 'thamDinh.soLieu.chuaCoBoPhatHanh')}
        </p>
        <p className="mt-1 text-sm text-warning-700">
          {t(chuaLinhVuc ? 'thamDinh.soLieu.chuaCoLinhVucMo' : 'thamDinh.soLieu.chuaCoBoPhatHanhMo')}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          icon={<FileSearchOutlined />}
          onClick={() => dieuHuong('/tham-dinh/tieu-chi')}
        >
          {t('thamDinh.soLieu.sangThuVien')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.thamDinhAi') }, { label: t('nav.muc.tongQuan') }]}
          titleIcon={<DashboardOutlined className="text-primary-600" />}
          title={t('thamDinh.tongQuanTieuDe')}
          subtitle={t('thamDinh.tongQuanMo')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void tq.refetch()}
              >
                <ReloadOutlined className={tq.isFetching ? 'animate-spin' : ''} />
              </Button>
              {q.phienTao && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setMoNop(true)}>
                  {t('thamDinh.nop.nutMoi')}
                </Button>
              )}
            </>
          }
        />

        <VungDuLieu
          dangTai={tq.isPending}
          loi={tq.error}
          dulieu={tq.data}
          rong={false}
          onThuLai={() => void tq.refetch()}
          rongTieuDe={t('thamDinh.phien.rong')}
        >
          {(du) => (
            <div className="space-y-4">
              <KhoiOnboarding du={du} />

              <Bento>
                <StatTile
                  nhan={t('thamDinh.soLieu.dangCham')}
                  so={soVN(du.dang_cham, locale)}
                  sub={t('thamDinh.soLieu.dangChamMo')}
                  icon={<SyncOutlined />}
                  tone="primary"
                />
                <StatTile
                  nhan={t('thamDinh.soLieu.choDuyet')}
                  so={soVN(du.cho_duyet, locale)}
                  sub={t('thamDinh.soLieu.choDuyetMo')}
                  icon={<ClockCircleOutlined />}
                  tone="warning"
                />
                <StatTile
                  nhan={t('thamDinh.soLieu.hoanTat')}
                  so={soVN(du.hoan_tat_30_ngay, locale)}
                  sub={t('thamDinh.soLieu.hoanTatMo')}
                  icon={<CheckCircleOutlined />}
                  tone="success"
                />
                {/*
                  * Chưa hoàn tất hồ sơ nào thì hiện gạch dài, không phải "0%": 0% đọc thành "chấm rồi và
                  * trượt hết", còn sự thật là chưa chấm hồ sơ nào.
                  */}
                <StatTile
                  nhan={t('thamDinh.soLieu.tyLeDat')}
                  so={du.hoan_tat_30_ngay > 0 ? `${soVN(Math.round(du.ty_le_dat_30_ngay * 100), locale)}%` : KHONG_CO}
                  sub={t('thamDinh.soLieu.tyLeDatMo', {
                    dieuKien: soVN(du.ket_luan_30_ngay.dat_co_dieu_kien, locale),
                    khongDat: soVN(du.ket_luan_30_ngay.khong_dat, locale),
                  })}
                  icon={<CheckCircleOutlined />}
                  tone="primary"
                />
              </Bento>

              <Bento>
                <Tile span="lg:col-span-6">
                  <TileHeader
                    icon={<ClockCircleOutlined />}
                    tone="orange"
                    title={t('thamDinh.soLieu.choDuyet')}
                    mo={t('thamDinh.soLieu.choDuyetKhoiMo')}
                  />
                  <TileBody className="p-0">
                    {du.danh_sach_cho_duyet.length === 0 ? (
                      <Empty title={t('thamDinh.soLieu.khongChoDuyet')} />
                    ) : (
                      <DataTable<Phien>
                        caption={t('thamDinh.soLieu.choDuyet')}
                        columns={cotChoDuyet}
                        dataSource={du.danh_sach_cho_duyet}
                        pagination={false}
                      />
                    )}
                  </TileBody>
                </Tile>

                <Tile span="lg:col-span-6">
                  <TileHeader
                    icon={<FallOutlined />}
                    tone="violet"
                    title={t('thamDinh.soLieu.tieuChiHayTruot')}
                    mo={t('thamDinh.soLieu.tieuChiHayTruotMo')}
                  />
                  <TileBody className="p-0">
                    {du.tieu_chi_hay_truot.length === 0 ? (
                      <Empty title={t('thamDinh.soLieu.khongTieuChiTruot')} />
                    ) : (
                      <DataTable<TieuChiHayTruot>
                        caption={t('thamDinh.soLieu.tieuChiHayTruot')}
                        rowKey={(r) => `${r.bo_id}-${r.ma}`}
                        columns={cotTruot}
                        dataSource={du.tieu_chi_hay_truot}
                        pagination={false}
                      />
                    )}
                  </TileBody>
                </Tile>
              </Bento>

              <Tile>
                <TileHeader
                  icon={<FileSearchOutlined />}
                  tone="primary"
                  title={t('thamDinh.soLieu.phienGanDay')}
                  action={
                    <Button variant="link" size="sm" onClick={() => dieuHuong('/tham-dinh/ho-so')}>
                      {t('thamDinh.soLieu.xemTatCa')}
                    </Button>
                  }
                />
                <TileBody className="p-0">
                  {du.gan_day.length === 0 ? (
                    <Empty title={t('thamDinh.phien.rong')} description={t('thamDinh.phien.rongMo')} />
                  ) : (
                    <DataTable<Phien>
                      caption={t('thamDinh.soLieu.phienGanDay')}
                      columns={cotChoDuyet}
                      dataSource={du.gan_day}
                      pagination={false}
                    />
                  )}
                </TileBody>
              </Tile>
            </div>
          )}
        </VungDuLieu>
      </div>

      <HopNopHoSo open={moNop} onClose={() => setMoNop(false)} onXong={() => dieuHuong('/tham-dinh/ho-so')} />
    </div>
  );
}
